import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { cacheGet, cacheSet } from '../../utils/cache';
import { useApp } from '../../contexts/AppContext';
import Tooltip from '../common/Tooltip';
import Pagination from '../common/Pagination';
import {
  CreditCard,
  Mail,
  Send,
  History,
  Search,
  Filter,
  X,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Inbox,
  Wallet,
  TrendingUp,
  Calendar,
  FileText,
  RotateCcw,
  Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUT_LABELS = {
  non_envoyee: 'Non envoyée',
  non_payee: 'Non payée',
  en_attente: 'En attente',
  relancee: 'Relancée',
  payee: 'Payée',
  en_litige: 'En litige',
};

const MODE_LABELS = {
  virement: 'Virement',
  cheque: 'Chèque',
  especes: 'Espèces',
  cb: 'Carte bancaire',
  autre: 'Autre',
};

const Comptabilite = () => {
  const { clients, showNotification, apiCall, settings } = useApp();
  // Données — initialisées depuis le cache pour éviter le "Chargement…"
  const [dashboard, setDashboard] = useState(() => cacheGet('cpt_dashboard', null));
  const [factures, setFactures] = useState(() => cacheGet('cpt_factures', []));
  const [paiements, setPaiements] = useState(() => cacheGet('cpt_paiements', []));
  // Premier chargement uniquement (jamais réactivé après)
  const [firstLoad, setFirstLoad] = useState(() => cacheGet('cpt_dashboard', null) === null);
  // Indicateur discret de rafraîchissement en arrière-plan
  const [refreshing, setRefreshing] = useState(false);
  // Flag pour ignorer les fetchs en cours au démontage (évite la race au logout)
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [showEnRetard, setShowEnRetard] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = parseInt(cacheGet('cpt_items_per_page', '25'), 10);
    return [10, 20, 25, 50, 100].includes(saved) ? saved : 25;
  });

  // Modales
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [showEnvoiGroupeModal, setShowEnvoiGroupeModal] = useState(false);
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [showHistoriquePaiements, setShowHistoriquePaiements] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [selectedFactures, setSelectedFactures] = useState([]);

  // Formulaires
  const [paiementForm, setPaiementForm] = useState({
    montant: '',
    mode_paiement: 'virement',
    reference: '',
    commentaire: '',
  });
  const [envoiGroupeForm, setEnvoiGroupeForm] = useState({
    sujet: '',
    message: '',
    programmer_relance: false,
    jours_avant_relance: 30,
  });
  const [messageRelance, setMessageRelance] = useState('');

  // Chargement
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadFactures();
    // Revenir à la page 1 dès qu'un filtre change pour éviter une page "vide"
    setCurrentPage(1);
  }, [clientFilter, statutFilter, searchTerm, showEnRetard]);

  const loadData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([loadDashboard(), loadFactures(), loadPaiements()]);
    } catch (e) {
      console.error('Erreur chargement données comptabilité:', e);
    }
    setRefreshing(false);
    setFirstLoad(false);
  };

  const loadDashboard = async () => {
    const r = await apiCall(`${API_URL}/api/comptabilite/dashboard`);
    if (r.ok && mountedRef.current) {
      const data = await r.json();
      setDashboard(data);
      cacheSet('cpt_dashboard', data);
    }
  };

  const loadFactures = async () => {
    let url = `${API_URL}/api/comptabilite/factures?`;
    if (clientFilter) url += `client_id=${clientFilter}&`;
    if (statutFilter) url += `statut_paiement=${statutFilter}&`;
    if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
    if (showEnRetard) url += `en_retard=true&`;
    const r = await apiCall(url);
    if (r.ok && mountedRef.current) {
      const data = await r.json();
      setFactures(data);
      // ne cacher la liste filtrée que si aucun filtre actif (liste de référence)
      if (!clientFilter && !statutFilter && !searchTerm && !showEnRetard) {
        cacheSet('cpt_factures', data);
      }
    }
  };

  const loadPaiements = async () => {
    const r = await apiCall(`${API_URL}/api/comptabilite/paiements`);
    if (r.ok && mountedRef.current) {
      const data = await r.json();
      setPaiements(data);
      cacheSet('cpt_paiements', data);
    }
  };

  // Handlers
  const handleEnregistrerPaiement = async (e) => {
    e.preventDefault();
    if (!selectedFacture) return;
    const r = await apiCall(`${API_URL}/api/comptabilite/paiement`, {
      method: 'POST',
      body: JSON.stringify({
        commande_id: selectedFacture.id,
        montant: parseFloat(paiementForm.montant),
        mode_paiement: paiementForm.mode_paiement,
        reference: paiementForm.reference,
        commentaire: paiementForm.commentaire,
      }),
    });
    if (r.ok) {
      showNotification('Paiement enregistré avec succès', 'success');
      setShowPaiementModal(false);
      setPaiementForm({ montant: '', mode_paiement: 'virement', reference: '', commentaire: '' });
      setSelectedFacture(null);
      loadData();
    } else {
      const err = await r.json();
      showNotification(err.detail || "Erreur lors de l'enregistrement", 'error');
    }
  };

  const handleChangerStatut = async (factureId, nouveauStatut) => {
    const r = await apiCall(`${API_URL}/api/comptabilite/facture/${factureId}/statut`, {
      method: 'PUT',
      body: JSON.stringify({ statut_paiement: nouveauStatut }),
    });
    if (r.ok) {
      showNotification(`Statut changé en "${STATUT_LABELS[nouveauStatut]}"`, 'success');
      loadData();
    } else {
      const err = await r.json();
      showNotification(err.detail || 'Erreur', 'error');
    }
  };

  const handleEnvoyerRelance = async () => {
    if (!selectedFacture) return;
    const r = await apiCall(`${API_URL}/api/comptabilite/relance/${selectedFacture.id}`, {
      method: 'POST',
      body: JSON.stringify({ message: messageRelance || null }),
    });
    if (r.ok) {
      showNotification('Relance envoyée avec succès', 'success');
      setShowRelanceModal(false);
      setMessageRelance('');
      setSelectedFacture(null);
      loadData();
    } else {
      const err = await r.json();
      showNotification(err.detail || "Erreur lors de l'envoi", 'error');
    }
  };

  const handleEnvoiGroupe = async () => {
    if (selectedFactures.length === 0) {
      showNotification('Sélectionnez au moins une facture', 'error');
      return;
    }
    const r = await apiCall(`${API_URL}/api/comptabilite/envoi-groupe`, {
      method: 'POST',
      body: JSON.stringify({
        facture_ids: selectedFactures,
        sujet: envoiGroupeForm.sujet || null,
        message: envoiGroupeForm.message || null,
        programmer_relance: envoiGroupeForm.programmer_relance,
        jours_avant_relance: envoiGroupeForm.jours_avant_relance,
      }),
    });
    if (r.ok) {
      const result = await r.json();
      showNotification(result.message, 'success');
      setShowEnvoiGroupeModal(false);
      setSelectedFactures([]);
      setEnvoiGroupeForm({ sujet: '', message: '', programmer_relance: false, jours_avant_relance: 30 });
      loadData();
    } else {
      const err = await r.json();
      showNotification(err.detail || 'Erreur', 'error');
    }
  };

  const toggleFacture = (id) =>
    setSelectedFactures((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const toggleAllFactures = () =>
    setSelectedFactures(selectedFactures.length === factures.length ? [] : factures.map((f) => f.id));

  // Pagination — slice les factures pour la page courante
  const totalFactures = factures.length;
  const totalPages = Math.max(1, Math.ceil(totalFactures / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const sliceStart = (safePage - 1) * itemsPerPage;
  const facturesPage = factures.slice(sliceStart, sliceStart + itemsPerPage);

  const handleItemsPerPageChange = (n) => {
    setItemsPerPage(n);
    cacheSet('cpt_items_per_page', String(n));
    setCurrentPage(1);
  };

  const resetFiltres = () => {
    setSearchTerm('');
    setClientFilter('');
    setStatutFilter('');
    setShowEnRetard(false);
  };

  // Helpers
  const setStatutChip = (statut, retard = false) => {
    if (retard) {
      setStatutFilter('');
      setShowEnRetard(true);
    } else {
      setStatutFilter(statut || '');
      setShowEnRetard(false);
    }
  };

  // Calculs pédagogiques
  const totalMontant = dashboard?.montant_total || 0;
  const totalPaye = dashboard?.montant_paye || 0;
  const tauxEncaissement = totalMontant > 0 ? Math.round((totalPaye / totalMontant) * 100) : 0;

  if (firstLoad && !dashboard) {
    return (
      <div className="comptabilite comptabilite-v2" data-testid="comptabilite-section">
        <div className="section-header">
          <h2>Comptabilité</h2>
        </div>
        <div className="loading">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="comptabilite comptabilite-v2" data-testid="comptabilite-section">
      {/* HEADER au format aligné avec les autres pages */}
      <div className="section-header">
        <h2>Comptabilité</h2>
        <div className="header-buttons">
          <button
            onClick={() => setShowHistoriquePaiements(true)}
            className="btn btn-secondary cpt-btn"
            data-testid="btn-historique-paiements"
          >
            <History size={16} />
            <span>Historique paiements</span>
          </button>
          <button
            onClick={() => setShowEnvoiGroupeModal(true)}
            className="btn btn-primary cpt-btn"
            disabled={selectedFactures.length === 0}
            data-testid="btn-envoi-groupe"
          >
            <Send size={16} />
            <span>Envoi groupé</span>
            {selectedFactures.length > 0 && (
              <span className="cpt-btn-badge">{selectedFactures.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Indicateur discret de rafraîchissement */}
      {refreshing && (
        <div className="cpt-refreshing" aria-live="polite" data-testid="cpt-refreshing">
          <span className="cpt-refreshing-dot" />
          Mise à jour…
        </div>
      )}

      {/* Bandeau pédagogique : que peut-on faire ici ? */}
      <div className="cpt-howto" data-testid="cpt-howto">
        <div className="cpt-howto-icon">
          <Info size={18} />
        </div>
        <div className="cpt-howto-body">
          <strong>Comment utiliser cette page ?</strong>
          <ol>
            <li><b>Filtrez</b> vos factures par statut via les pastilles ci-dessous (Toutes / Non envoyées / Non payées / En retard / Payées)</li>
            <li><b>Sélectionnez plusieurs factures</b> à l'aide des cases pour les <b>envoyer en lot</b> par email avec PDF joint</li>
            <li>Sur chaque facture : <CreditCard size={12} /> enregistrer un paiement, <Mail size={12} /> envoyer une relance, ou changer son statut directement dans la colonne Statut</li>
          </ol>
        </div>
      </div>

      {/* HERO — Vue d'ensemble financière */}
      {dashboard && (
        <section className="cpt-hero">
          <div className="cpt-hero-cards">
            <div className="cpt-stat-card">
              <div className="cpt-stat-icon cpt-stat-icon-primary">
                <FileText size={20} />
              </div>
              <div className="cpt-stat-body">
                <span className="cpt-stat-label">Total facturé</span>
                <span className="cpt-stat-value">{formatCurrency(totalMontant, settings?.devise)}</span>
                <span className="cpt-stat-hint">{dashboard.total_factures} facture{dashboard.total_factures > 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="cpt-stat-card cpt-stat-success">
              <div className="cpt-stat-icon cpt-stat-icon-success">
                <Wallet size={20} />
              </div>
              <div className="cpt-stat-body">
                <span className="cpt-stat-label">Déjà encaissé</span>
                <span className="cpt-stat-value">{formatCurrency(totalPaye, settings?.devise)}</span>
                <span className="cpt-stat-hint">{tauxEncaissement}% du total facturé</span>
              </div>
            </div>

            <div className="cpt-stat-card cpt-stat-warning">
              <div className="cpt-stat-icon cpt-stat-icon-warning">
                <Clock size={20} />
              </div>
              <div className="cpt-stat-body">
                <span className="cpt-stat-label">Reste à encaisser</span>
                <span className="cpt-stat-value">{formatCurrency(dashboard.montant_impaye, settings?.devise)}</span>
                <span className="cpt-stat-hint">{(dashboard.par_statut?.non_payees || 0) + (dashboard.par_statut?.en_attente || 0) + (dashboard.par_statut?.relancees || 0)} facture(s) en attente</span>
              </div>
            </div>

            <div className="cpt-stat-card">
              <div className="cpt-stat-icon cpt-stat-icon-info">
                <TrendingUp size={20} />
              </div>
              <div className="cpt-stat-body">
                <span className="cpt-stat-label">Encaissé ce mois</span>
                <span className="cpt-stat-value">{formatCurrency(dashboard.encaissements_mois, settings?.devise)}</span>
                <span className="cpt-stat-hint">
                  <Calendar size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  Période en cours
                </span>
              </div>
            </div>
          </div>

          {/* Barre de progression pédagogique */}
          <div className="cpt-progress-block">
            <div className="cpt-progress-head">
              <span className="cpt-progress-title">
                <Info size={14} /> Progression de l'encaissement
              </span>
              <span className="cpt-progress-percent">{tauxEncaissement}%</span>
            </div>
            <div className="cpt-progress-bar">
              <div
                className="cpt-progress-fill"
                style={{ width: `${tauxEncaissement}%` }}
              />
            </div>
            <div className="cpt-progress-foot">
              <span>{formatCurrency(totalPaye, settings?.devise)} encaissés</span>
              <span>sur {formatCurrency(totalMontant, settings?.devise)}</span>
            </div>
          </div>
        </section>
      )}

      {/* FILTRES RAPIDES PAR STATUT */}
      {dashboard && (
        <section className="cpt-quick-filters" aria-label="Filtres rapides">
          <button
            className={`cpt-chip ${!statutFilter && !showEnRetard ? 'cpt-chip-active' : ''}`}
            onClick={() => setStatutChip('')}
            data-testid="chip-toutes"
          >
            <FileText size={14} /> Toutes
            <span className="cpt-chip-count">{dashboard.total_factures}</span>
          </button>
          <button
            className={`cpt-chip ${statutFilter === 'non_envoyee' ? 'cpt-chip-active' : ''}`}
            onClick={() => setStatutChip('non_envoyee')}
            data-testid="chip-non-envoyees"
          >
            <Inbox size={14} /> Non envoyées
            <span className="cpt-chip-count">{dashboard.par_statut?.non_envoyees || 0}</span>
          </button>
          <button
            className={`cpt-chip ${statutFilter === 'non_payee' ? 'cpt-chip-active' : ''}`}
            onClick={() => setStatutChip('non_payee')}
            data-testid="chip-non-payees"
          >
            <Clock size={14} /> Non payées
            <span className="cpt-chip-count">{dashboard.par_statut?.non_payees || 0}</span>
          </button>
          <button
            className={`cpt-chip cpt-chip-danger ${showEnRetard ? 'cpt-chip-active' : ''}`}
            onClick={() => setStatutChip('', true)}
            data-testid="chip-en-retard"
          >
            <AlertTriangle size={14} /> En retard
            <span className="cpt-chip-count">{dashboard.factures_en_retard}</span>
          </button>
          <button
            className={`cpt-chip ${statutFilter === 'payee' ? 'cpt-chip-active' : ''}`}
            onClick={() => setStatutChip('payee')}
            data-testid="chip-payees"
          >
            <CheckCircle2 size={14} /> Payées
            <span className="cpt-chip-count">{dashboard.par_statut?.payees || 0}</span>
          </button>
        </section>
      )}

      {/* RECHERCHE + FILTRE CLIENT */}
      <section className="cpt-toolbar">
        <div className="cpt-search-wrap">
          <Search size={16} className="cpt-search-icon" />
          <input
            type="text"
            placeholder="Rechercher par n° de facture ou client…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="cpt-search-input"
            data-testid="search-factures"
          />
          {searchTerm && (
            <button
              className="cpt-search-clear"
              onClick={() => setSearchTerm('')}
              title="Effacer la recherche"
              aria-label="Effacer la recherche"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="cpt-filter-wrap">
          <Filter size={14} className="cpt-filter-icon" />
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="cpt-filter-select"
            data-testid="filter-client"
          >
            <option value="">Tous les clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom_entreprise}
              </option>
            ))}
          </select>
        </div>
        {(searchTerm || clientFilter || statutFilter || showEnRetard) && (
          <button className="cpt-reset-btn" onClick={resetFiltres} title="Réinitialiser les filtres">
            <RotateCcw size={14} /> Réinitialiser
          </button>
        )}
      </section>

      {/* TABLEAU FACTURES */}
      <section className="cpt-table-card">
        <div className="cpt-table-head">
          <h3 className="cpt-table-title">
            {factures.length} facture{factures.length > 1 ? 's' : ''}
            {selectedFactures.length > 0 && (
              <span className="cpt-selection-info"> · {selectedFactures.length} sélectionnée{selectedFactures.length > 1 ? 's' : ''}</span>
            )}
          </h3>
          {factures.length > 0 && (
            <label className="cpt-select-all">
              <input
                type="checkbox"
                checked={selectedFactures.length === factures.length}
                onChange={toggleAllFactures}
              />
              Tout sélectionner
            </label>
          )}
        </div>

        <div className="table-container cpt-table-wrap">
          <table className="cpt-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>N° Facture</th>
                <th>Client</th>
                <th>Date</th>
                <th className="cpt-num">Montant</th>
                <th className="cpt-num">Reste</th>
                <th>
                  <span className="cpt-th-with-info">
                    Statut
                    <Tooltip
                      multiline
                      content="Cycle d'une facture : 1) Non envoyée → 2) En attente (envoyée au client) → 3) Relancée si retard → 4) Payée. 'En litige' = désaccord client."
                    >
                      <span
                        className="cpt-th-info"
                        data-testid="statut-info-tip"
                      >
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </span>
                </th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {facturesPage.map((facture) => (
                <tr
                  key={facture.id}
                  className={facture.en_retard ? 'row-en-retard' : ''}
                  data-testid={`facture-row-${facture.id}`}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedFactures.includes(facture.id)}
                      onChange={() => toggleFacture(facture.id)}
                    />
                  </td>
                  <td>
                    <strong>{facture.numero_facture}</strong>
                  </td>
                  <td>{facture.nom_client}</td>
                  <td className="cpt-cell-date">
                    {formatDate(facture.date_facture)}
                    {facture.en_retard && (
                      <span className="cpt-retard-inline" title="Retard de paiement">
                        <AlertTriangle size={12} /> {facture.jours_depuis_facture}j
                      </span>
                    )}
                  </td>
                  <td className="cpt-num">{formatCurrency(facture.total, settings?.devise)}</td>
                  <td className={`cpt-num ${facture.reste_a_payer > 0 ? 'montant-reste' : 'montant-paye'}`}>
                    {formatCurrency(facture.reste_a_payer, settings?.devise)}
                  </td>
                  <td>
                    <select
                      value={facture.statut_paiement || 'non_envoyee'}
                      onChange={(e) => handleChangerStatut(facture.id, e.target.value)}
                      className={`cpt-statut-select cpt-statut-${(facture.statut_paiement || 'non_envoyee').replace('_', '-')}`}
                      title="Changer le statut"
                      data-testid={`statut-select-${facture.id}`}
                    >
                      {Object.entries(STATUT_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {facture.statut_paiement !== 'payee' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedFacture(facture);
                              setPaiementForm({
                                montant: facture.reste_a_payer.toString(),
                                mode_paiement: 'virement',
                                reference: '',
                                commentaire: '',
                              });
                              setShowPaiementModal(true);
                            }}
                            className="btn-action btn-success"
                            title="Enregistrer un paiement"
                            data-testid={`btn-paiement-${facture.id}`}
                          >
                            <CreditCard size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedFacture(facture);
                              setShowRelanceModal(true);
                            }}
                            className="btn-action btn-warning"
                            title="Envoyer une relance"
                            data-testid={`btn-relance-${facture.id}`}
                          >
                            <Mail size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {factures.length === 0 && (
                <tr>
                  <td colSpan="8" className="cpt-empty">
                    <Inbox size={28} />
                    <p>Aucune facture trouvée</p>
                    <small>Essayez d'ajuster vos filtres</small>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — affichée seulement si > 1 page */}
        {totalFactures > itemsPerPage && (
          <div className="cpt-pagination-wrap" data-testid="cpt-pagination">
            <Pagination
              currentPage={safePage}
              totalItems={totalFactures}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}
      </section>

      {/* Modal Paiement */}
      {showPaiementModal && selectedFacture && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3><CreditCard size={18} /> Enregistrer un paiement</h3>
              <button className="modal-close" onClick={() => setShowPaiementModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-info">
              <div className="cpt-info-row"><span>Facture</span><strong>{selectedFacture.numero_facture}</strong></div>
              <div className="cpt-info-row"><span>Client</span><strong>{selectedFacture.nom_client}</strong></div>
              <div className="cpt-info-row"><span>Total</span><strong>{formatCurrency(selectedFacture.total, settings?.devise)}</strong></div>
              <div className="cpt-info-row"><span>Déjà payé</span><strong className="montant-paye">{formatCurrency(selectedFacture.montant_paye || 0, settings?.devise)}</strong></div>
              <div className="cpt-info-row"><span>Reste à payer</span><strong className="montant-reste">{formatCurrency(selectedFacture.reste_a_payer, settings?.devise)}</strong></div>
            </div>
            <form onSubmit={handleEnregistrerPaiement}>
              <div className="form-group">
                <label>Montant du paiement *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedFacture.reste_a_payer}
                  value={paiementForm.montant}
                  onChange={(e) => setPaiementForm({ ...paiementForm, montant: e.target.value })}
                  required
                  data-testid="input-montant-paiement"
                />
              </div>
              <div className="form-group">
                <label>Mode de paiement *</label>
                <select
                  value={paiementForm.mode_paiement}
                  onChange={(e) => setPaiementForm({ ...paiementForm, mode_paiement: e.target.value })}
                  required
                >
                  {Object.entries(MODE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Référence</label>
                <input
                  type="text"
                  value={paiementForm.reference}
                  onChange={(e) => setPaiementForm({ ...paiementForm, reference: e.target.value })}
                  placeholder="N° chèque, réf. virement…"
                />
              </div>
              <div className="form-group">
                <label>Commentaire</label>
                <textarea
                  value={paiementForm.commentaire}
                  onChange={(e) => setPaiementForm({ ...paiementForm, commentaire: e.target.value })}
                  placeholder="Optionnel"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowPaiementModal(false)} className="btn btn-secondary">
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" data-testid="btn-confirm-paiement">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Relance */}
      {showRelanceModal && selectedFacture && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3><Mail size={18} /> Envoyer une relance</h3>
              <button className="modal-close" onClick={() => setShowRelanceModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-info">
              <div className="cpt-info-row"><span>Facture</span><strong>{selectedFacture.numero_facture}</strong></div>
              <div className="cpt-info-row"><span>Client</span><strong>{selectedFacture.nom_client}</strong></div>
              <div className="cpt-info-row"><span>Reste à payer</span><strong className="montant-reste">{formatCurrency(selectedFacture.reste_a_payer, settings?.devise)}</strong></div>
              {selectedFacture.nb_relances > 0 && (
                <div className="cpt-info-row"><span>Relances précédentes</span><strong>{selectedFacture.nb_relances}</strong></div>
              )}
            </div>
            <div className="form-group">
              <label>Message personnalisé (optionnel)</label>
              <textarea
                value={messageRelance}
                onChange={(e) => setMessageRelance(e.target.value)}
                rows={6}
                placeholder="Laissez vide pour utiliser le message par défaut"
              />
            </div>
            <div className="form-actions">
              <button onClick={() => setShowRelanceModal(false)} className="btn btn-secondary">
                Annuler
              </button>
              <button onClick={handleEnvoyerRelance} className="btn btn-primary" data-testid="btn-confirm-relance">
                <Mail size={16} /> Envoyer la relance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Envoi Groupé */}
      {showEnvoiGroupeModal && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3><Send size={18} /> Envoi groupé de factures</h3>
              <button className="modal-close" onClick={() => setShowEnvoiGroupeModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-info">
              <div className="cpt-info-row">
                <span>Factures sélectionnées</span>
                <strong>{selectedFactures.length}</strong>
              </div>
            </div>
            <div className="form-group">
              <label>Sujet de l'email</label>
              <input
                type="text"
                value={envoiGroupeForm.sujet}
                onChange={(e) => setEnvoiGroupeForm({ ...envoiGroupeForm, sujet: e.target.value })}
                placeholder="Laissez vide pour le sujet par défaut"
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                value={envoiGroupeForm.message}
                onChange={(e) => setEnvoiGroupeForm({ ...envoiGroupeForm, message: e.target.value })}
                rows={6}
                placeholder="Laissez vide pour le message par défaut"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={envoiGroupeForm.programmer_relance}
                  onChange={(e) =>
                    setEnvoiGroupeForm({ ...envoiGroupeForm, programmer_relance: e.target.checked })
                  }
                />
                Programmer une relance automatique
              </label>
            </div>
            {envoiGroupeForm.programmer_relance && (
              <div className="form-group">
                <label>Relancer après (jours)</label>
                <input
                  type="number"
                  min="1"
                  value={envoiGroupeForm.jours_avant_relance}
                  onChange={(e) =>
                    setEnvoiGroupeForm({
                      ...envoiGroupeForm,
                      jours_avant_relance: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </div>
            )}
            <div className="form-actions">
              <button onClick={() => setShowEnvoiGroupeModal(false)} className="btn btn-secondary">
                Annuler
              </button>
              <button onClick={handleEnvoiGroupe} className="btn btn-primary" data-testid="btn-confirm-envoi-groupe">
                <Send size={16} /> Envoyer ({selectedFactures.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique Paiements */}
      {showHistoriquePaiements && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3><History size={18} /> Historique des paiements</h3>
              <button className="modal-close" onClick={() => setShowHistoriquePaiements(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Facture</th>
                    <th>Client</th>
                    <th className="cpt-num">Montant</th>
                    <th>Mode</th>
                    <th>Référence</th>
                    <th>Par</th>
                  </tr>
                </thead>
                <tbody>
                  {paiements.map((p) => (
                    <tr key={p.id}>
                      <td>{formatDate(p.date_paiement)}</td>
                      <td>{p.numero_facture}</td>
                      <td>{p.nom_client}</td>
                      <td className="cpt-num montant-paye">{formatCurrency(p.montant, settings?.devise)}</td>
                      <td>{MODE_LABELS[p.mode_paiement] || p.mode_paiement}</td>
                      <td>{p.reference || '—'}</td>
                      <td>{p.enregistre_par}</td>
                    </tr>
                  ))}
                  {paiements.length === 0 && (
                    <tr>
                      <td colSpan="7" className="cpt-empty">
                        <Inbox size={28} />
                        <p>Aucun paiement enregistré</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button onClick={() => setShowHistoriquePaiements(false)} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comptabilite;
